import { createContext, useState, useContext } from 'react';
import { UserContext } from '@mirinae/react-auth';

import { UserAgent } from '@mirinae/js-utils';

import { useAppVersionStore } from '@mirinae/shared/stores/appVersion';
import GlobalContext, { useGlobalContext } from '@mirinae/lessons/modules/contexts/global';
import { myLessonsAPI } from '@mirinae/lessons/modules/apis/myLessons';
import { lessonsPaths, explorerPaths, membersPaths, authPaths, apiPaths } from '@mirinae/shared/defines/paths';
import {
    loadAndUpdateLessonNodeField,
    refreshNodesInNodeMap,
    loadLessonObject,
    loadAndBackupLesson,
    postLessonEvent,
    loadAndExportLesson,
} from '@mirinae/lessons/modules/libs/lessonLib';
import { useNavigate } from 'react-router-dom';
import { stateMap } from '@mirinae/lessons/components/card/LessonCard';
import httpAPI from '@mirinae/lessons/modules/apis/http';
import { apiServer } from '@mirinae/shared/defines/paths';
import {
    deflateNode,
    getFigmaTemplates,
    conformToFigmaLayout,
    checkAndRepairLessonTree,
    wireNodeTree,
    lessonFixUps,
} from '../../editor/modules/libs/lessonLib';
import Localization from '@mirinae/shared/il8n';

import COURSES, {overwriteLanguageCourses} from '@mirinae/lessons/modules/defines/course_data';

const isMobile = !UserAgent.os.isOthers;

const LessonsContext = createContext({});

// initializing lesson state
const lessonTable = {
    starter: { set: 'grammar', group: 'starter', title: 'Starter', count: 0, link: lessonsPaths.starter },
    beginner: { set: 'grammar', group: 'beginner', title: 'Beginner', count: 0, link: lessonsPaths.beginner },
    intermediate: { set: 'grammar', group: 'intermediate', title: 'Intermediate', count: 0, link: lessonsPaths.intermediate },
    advanced: { set: 'grammar', group: 'advanced', title: 'Advanced', count: 0, link: lessonsPaths.advanced },
    idiom: { set: 'culture', group: 'idiom', title: 'Idioms', count: 0, link: lessonsPaths.idiom },
    neologism: { set: 'culture', group: 'neologism', title: 'Neologisms', count: 0, link: lessonsPaths.neologism },
    proverb: { set: 'culture', group: 'proverb', title: 'Proverbs', count: 0, link: lessonsPaths.proverb },
};

const lessonStateInit = {
    lessonCourses: {},
    lessonTable,
    lessonKinds: {
        grammar: [lessonTable.starter, lessonTable.beginner, lessonTable.intermediate, lessonTable.advanced],
        culture: [lessonTable.idiom, lessonTable.neologism, lessonTable.proverb],
    },
    // lessonCounts: {},
    allLessons: [],
    myLessons: [],
    myLessonMap: {},
    lessonsToStart: [],
    recentLessons: [], // active + completed
    activeLessons: [],
    completedLessons: [],
    searchText: '',
    initializing: false,
    editors: [],

    // dialog state (replicated from editor context (for now)
    showConfirm: false, confirmMessage: '',
    deletingNode: null,
    showAlert: false,
    alertMessage: '',
    hideOK: false,
    showBackupDialog: false,
    showAssignPatternDialog: false, lessonToAssign: null,
    adminListContextMenu: { open:false, anchorEl: null },
    showGlobalSRDialog: false,
    editJIRA: '',
};

// DB nodeMap updates need to be serialized to avoid out-of-order state corruption
const dbUpdateQueue = [];
let dbQueueCount = 0;
let alertDelayTimer = null;
let showAlertCount = 0;

// Establishes LessonsContext via a custom use-hook.
export function useLessonsContext() {
    const [lessonState, setLessonState] = useState(lessonStateInit);
    const [userContext] = useContext(UserContext);
    const { user } = userContext;
    const navigate = useNavigate();
    const { appVersion, isProEnabledVersion } = useAppVersionStore();
    const globalContext = useGlobalContext(appVersion, isProEnabledVersion);
    const global = useContext(GlobalContext);
    const language = user.preferences?.language || Localization.getLanguage();

    const guestMode = () => user.name === undefined || user.name === '' || user.role === 'guest';
    const adminUser = () => user && user.role && user.role.match(/superuser|admin|superadmin/);
    const lessonCreator = () => user && user.role && user.role.match(/superuser|admin|superadmin|lessoncreator/);
    const lessonEditor = () => user && user.role && user.role.match(/superuser|admin|superadmin|lessoneditor|lessoncreator/);
    const onAdminPage = () => window.location.pathname.match(/\/admin$/);

    const updateLessonState = (update) => {
        setLessonState((prev) => ({ ...prev, ...update }));
    };

    const initialize = () => setLessonState(lessonStateInit);

    const numberOrder = (l) => (Number.isNaN(Number.parseInt(l.lessonNode.number, 10)) ? 1e6 : Number.parseInt(l.lessonNode.number, 10)) + (l.lessonNode.backupForNodeID || l.lessonNode.status === 'Backup' ? 2e6 : 0);
    const lessonOrder = (l) => ({ starter: 1, beginner: 10000, intermediate: 20000, advanced: 30000 }[l.lessonNode.group.toLowerCase()] || 100000) + numberOrder(l);
    const numberSort = (l1, l2) => lessonOrder(l1) - lessonOrder(l2);
    const completed = (lesson) => lesson.lessonTracker.state.match(/finished|reviewed/i);
    // sort active first, then completed and by recency of activity in each
    const recentsSort = (l1, l2) => (l1.lessonTracker.updated >= l2.lessonTracker.updated ? -1 : 1);

    const uniq = d => [...new Set(d)];

    const loadLessonLists = (options = { }) => {
        if (!lessonState.initializing) {
            initialize();
            updateLessonState({ initializing: true });

            const disp = localStorage.getItem('lesson-list-display');
            const displaySettings = disp ? JSON.parse(disp) : { };

            const langToLoad = options.language ? options.language : (onAdminPage() ? displaySettings.language || 'en' : language);
            const langFilter = langToLoad === 'en' ? { $or: [{ language: { $in: ['en', 'english'] } }, { language: { $exists: false } }] } : { language: langToLoad }; // { $or: [{ language: { $in: ['en', 'english'] } }, { language: { $exists: false } }] }; //
            const myLessonsLangFilter = langToLoad === 'en' ? { $or: [{ 'lesson.language': { $in: ['en', 'english'] } }, { 'lesson.language': { $exists: false } }] } : { 'lesson.language': langToLoad }; // { $or: [{ language: { $in: ['en', 'english'] } }, { language: { $exists: false } }] }; //
            let display; try { display = JSON.parse(localStorage.getItem('lesson-list-display')) || { }; } catch (e) { display = { }; }

            overwriteLanguageCourses(langToLoad);
            const lessonCourses = {...COURSES};

            myLessonsAPI.lessonList({ display, isMobile, filter: langFilter }).then(response => {
                const { lessons: allLessons, editors } = response.data;
                const publishedLessons = allLessons.filter((l) => l.lessonNode.status === 'Published' && !l.private && !l.replaced);
                allLessons.forEach(d => d.lessonNode.levelTitle = d.lessonNode.levelTitle.toLowerCase().replace(/^lv\./, '')); // patch old level titles
                
                if (allLessons.length > 0) {
                    lessonCourses.kinds.forEach((k) => {
                        k.children.forEach((c) => {
                            const groupAndLevelLessons = publishedLessons.filter((l) => l.lessonNode.group === k.group && l.lessonNode.levelTitle === c.levelTitle);
                            c.children = groupAndLevelLessons;
                            c.total = groupAndLevelLessons.length || 0;
                        });
                    });
                    lessonCourses.total = publishedLessons?.length || 0;
                }

                // get my lessons, rebuild lesson state  lists
                myLessonsAPI.getMyLessons({ display, isMobile, filter: myLessonsLangFilter })
                .then(response => {
                    const { lessons: myLessons } = response.data;
                    myLessons.forEach(d => d.lessonNode.levelTitle = d.lessonNode.levelTitle.toLowerCase().replace(/^lv\./, ''));
                    myLessons.sort(recentsSort);
                    if (myLessons.length > 0) {
                        lessonCourses.kinds.forEach((k) => {
                            k.children.forEach((d) => {
                                d.completed = myLessons.filter((l) => l.lessonNode.group === k.group && l.lessonNode.levelTitle === d.levelTitle && l.lessonNode.status === 'Published' && l.lessonTracker.state.match(/finished|reviewed/i)).length;
                                if (d.children.find((d) => d._id === myLessons[0]._id)) {
                                    lessonCourses.latestCourse = d;
                                    lessonCourses.latestLesson = myLessons[0];
                                }
                            });
                        });
                        lessonCourses.completed = myLessons.filter((l) =>  l.lessonNode.status === 'Published' && l.lessonTracker.state.match(/finished|reviewed/i)).length;
                    }

                    if (allLessons.length > 0 && myLessons.length > 0 && Object.keys(lessonCourses.latestLesson).length > 0) {
                        let finded = false;
                        let i = 0;
                        for(i; i < publishedLessons.length; i++) {
                            // if(allLessons[i]?.lessonNode?.group !== lessonCourses?.latestLesson?.lessonNode?.group
                            //     && allLessons[i]?.lessonNode?.levelTitle !== lessonCourses?.latestLesson?.lessonNode?.levelTitle) {
                            //     continue;
                            // }
                            if(publishedLessons[i]._id === lessonCourses.latestLesson._id) {
                                finded = true;
                                continue;
                            }
                            if(finded) {
                                lessonCourses.nextLesson = publishedLessons[i];
                                break;
                            }
                        }
                        if(i === publishedLessons.length) {
                            lessonCourses.nextLesson = publishedLessons[0];
                        }
                    }

                    if (myLessons.length > 0) {
                        lessonCourses.summary.forEach((k) => {
                            const group = k.group;
                            k.children.forEach((d) => {
                                const level = d.levelTitle;
                                const children = myLessons.filter((m) => m.lessonNode.group === group && m.lessonNode.levelTitle === level && m.lessonNode.status === 'Published' && m.lessonTracker.state.match(/finished|reviewed/i));
                                if (children) {
                                    d.children = children;
                                }
                            });
                        });
                    }
                    if (myLessons.length === 0 || !lessonCourses.latestLesson.lessonNode) {
                        const kinds = lessonCourses.kinds.find((k) => k.group === (user?.preferences?.lessonsLevel?.replace('unknown', 'Starter') || 'Starter'));
                        if (kinds && kinds?.children?.length > 0) {
                            const course = kinds.children[0];
                            lessonCourses.latestCourse = course;
                            lessonCourses.latestLesson = course.children[0];
                        }
                    }
                    buildLessonLists(allLessons, myLessons, lessonCourses);
                    updateLessonState({ initializing: false, editors });
                });
            });
        }
    };

    const buildLessonLists = (allLessons, myLessons, lessonCourses) => {
        const underEdit = {};
        allLessons.forEach(d => {
            if (d.editing) underEdit[d._id] = d.editing;
        });
        const lessonTable = { ...lessonState.lessonTable };
        Object.values(lessonTable).map(lte => lte.count = 0);
        const visibleLessons = allLessons.filter(lesson => !lesson.lessonNode.status.match(/Backup|Retired|Template/));
        const visibleMyLessons = myLessons.filter(lesson => !lesson.lessonNode.status.match(/Backup|Retired|Template/));
        visibleLessons.forEach(l => {
            if (l.lessonNode.group) {
                lessonTable[l.lessonNode.group.toLowerCase()].count += 1;
            }
        });
        const myLessonMap = {};
        visibleMyLessons.forEach(ml => {
            ml.editing = underEdit[ml._id];
            myLessonMap[ml._id] = ml;
        });
        const loggedIn = user.role !== 'guest';
        updateLessonState({
            lessonTable,
            allLessons,
            myLessonMap,
            lessonCourses: lessonCourses,
            myLessons: visibleMyLessons,
            recentLessons: visibleMyLessons.filter(() => loggedIn).sort(recentsSort),
            activeLessons: visibleMyLessons.filter(lesson => loggedIn && !completed(lesson)).sort(recentsSort),
            completedLessons: visibleMyLessons.filter(lesson => loggedIn && completed(lesson)),
            lessonsToStart: visibleLessons.filter(lesson => !loggedIn || !myLessonMap[lesson._id]).sort(numberSort),
        });
        // build template lesson list for the editor
        globalContext.update({ templateLessons: allLessons.filter(lesson => lesson.lessonNode.status === 'Template') });
    };

    // for now, some of this code is replicated from the editor context to allow  certain editor-related actions in the
    // admin menu and lesson list.  later, we should refactor into common utility libraries, though it will be tricky
    const queueNodeMapUpdate = async (update, options) => {
        dbUpdateQueue.push(update);
        dbQueueCount += 1;
        if (dbQueueCount === 1) {
            if (!(options && options.noAlerts)) setShowAlert(true, 'Saving changes, please wait...', 3000, true);
            while (dbUpdateQueue.length > 0) {
                console.log('dbUpdateQueue', dbUpdateQueue.length, dbQueueCount);
                const user = user ? { name: user.name, _id: user._id } : {};
                const data = { data: { updates: dbUpdateQueue.splice(0).map(d => d.data), user, ...(options || {}) } };
                // data.data.nodes.filter(n => n.nodeClass === 'section').forEach(n => console.log('section db update', n));
                try {
                    // this thing is bundling nodelists in update calls, but necessarily serializing those bundle updates
                    // eslint-disable-next-line no-await-in-loop
                    const response = await httpAPI(apiServer, apiPaths.updateNodeMapByGuid, data);
                    dbQueueCount -= data.data.updates.length;
                    // returns mongo objects for any new Lesson objects, update allLessons list & rebuild derived lesson lists
                    const { lessonObjects } = response.data;
                    if (lessonObjects && lessonObjects.length > 0) {
                        const allLessons = [...lessonState.allLessons];
                        lessonObjects.forEach(({ lessonObject }) => allLessons.push(lessonObject));
                        buildLessonLists(allLessons, lessonState.myLessons);
                    }
                } catch (e) {
                    setShowAlert(false);
                    setShowAlert(true, `Database update failed: ${e}`);
                    console.log(`\n**** Database update failed: ${e}\n`);
                    dbQueueCount -= data.data.updates.length;
                    return;
                }
            }
            if (!(options && options.noAlerts)) setShowAlert(false);
            console.log('db queue empty', dbUpdateQueue.length, dbQueueCount);
            dbQueueCount = 0;
        }
    };

    const setShowAlert = (show, message, delay, hideOK) => {
        console.log('setShowAlert -', show, message, delay);
        if (show) {
            if (delay) {
                if (!alertDelayTimer) {
                    alertDelayTimer = setTimeout(() => {
                        if (hideOK) showAlertCount += 1;
                        updateLessonState({ showAlert: true, alertMessage: message, hideOK });
                    }, delay);
                } else {
                    updateLessonState({ alertMessage: message });
                }
            } else {
                if (hideOK) showAlertCount += 1;
                updateLessonState({ showAlert: true, alertMessage: message, hideOK });
            }
        } else if (!lessonState.hideOK) {
            if (alertDelayTimer)
                clearTimeout(alertDelayTimer);
            alertDelayTimer = null;
            showAlertCount -= 1;
            if (showAlertCount <= 0) {
                updateLessonState({ showAlert: false, alertMessage: '', hideOK: false });
                showAlertCount = 0;
            }
        }
    };

    const updateDBNodeMap = (nodes, deletedNodes, options = {}) => {
        const data = { data: {
            nodes: options.alreadyFlat ? nodes : nodes.filter(n => n.nodeID !== 'root').map(n => ({ ...deflateNode(n) })),
            deletedNodes: options.alreadyFlat ? deletedNodes || [] : (deletedNodes || []).filter(n => n.nodeID !== 'root').map(n => ({ ...deflateNode(n) })),
        } };
        if (options.applyFixups) {
            const updatedNodes = new Set();
            const addedNodes = [];
            data.data.nodes.forEach(node => {
                if (node.nodeID !== 'root')
                    lessonFixUps(options.nodeMap, node, updatedNodes, addedNodes);
            });
            data.data.nodes.push(...addedNodes);
        }
        queueNodeMapUpdate(data, options);
    };

    const updateLessonNodeField = async (lesson, field, value) => {
        if (await loadAndUpdateLessonNodeField(lesson, field, value, user)) {
            lesson.lessonNode[field] = value;
            updateLessonState({ allLessons: lessonState.allLessons.filter(l => l.lessonNode.status !== 'Retired') });
        } else {
            setShowAlert(true, 'Unable to update lesson, maybe being currently edited');
        }
    };

    const lessonLink = (lesson, editMode) => {
        let link = `${lessonsPaths.player}/${lesson._id}`;
        const myLesson = lessonState.myLessonMap[lesson._id];
        if (myLesson && myLesson.lessonTracker && myLesson.lessonTracker.state.match(/progressing|reviewing/) && myLesson.lastEvent.action.match(/next|complete/))
            link += `/${myLesson.lastEvent.action === 'complete' ? myLesson.lastEvent.context.page : myLesson.lastEvent.details.to.page}`;
        return link + (editMode ? '?edit=1' : '');
    };

    const editLesson = async (lesson) => {
        // postLessonEvent(data.lessonObject, { action: 'edit', context: { lesson: data.lessonObject.lessonNode.nodeID } });
        if (user) {
            global.update({ showExitConfirmDialog: false });
            const continueEdit = async () => {
                await loadAndBackupLesson(lesson, user, { note: 'auto-save on edit' }); // take a safety backup first
                const myLesson = lessonState.myLessonMap[lesson._id];
                const state = myLesson && myLesson.lessonTracker ? myLesson.lessonTracker.state : 'start';
                const action = stateMap[state].action;
                postLessonEvent(lesson, { action, context: { lesson: lesson.lessonNode.nodeID } });
                navigate(lessonLink(lesson, true));
            };

            // HEY!! for now, we have disabled all edit-lock checks for checking convenience, since essentially all high-volume lesson editing is complete
            // if (lesson.lessonNode.status.match(/Ready|Published/i)) {
            //     setShowAlert(true, 'BLOCKED: Lessons in the Ready or Published state cannot be edited without changing them to a different status.');
            //     return;
            // }
            try {
                // const lock = await httpAPI('', apiPaths.editLock, { data: { lessonID: lesson._id, userID: user && user._id, action: 'acquire' } });
                const lock = await httpAPI('', apiPaths.editLock, { data: { lessonID: lesson._id, userID: user && user._id, action: 'forceAcquire' } });
                console.log('acquire lock', lock);
            } catch (e) {
                const { lock } = e.data;
                console.log('failed to acquire lock', lock);
                updateLessonState({
                    showConfirm: true,
                    confirmMessage: `WARNING: Lesson currently being edited by ${lock.editor.name}.  If you want to force edit mode anyway, hit OK`,
                    confirmCallback: async () => {
                        await httpAPI('', apiPaths.editLock, { data: { lessonID: lesson._id, userID: user && user._id, action: 'forceAcquire' } });
                        continueEdit();
                    },
                });
                return;
            }
            continueEdit();
        }
    };

    const releaseEditLock = async (lesson) => {
        return await httpAPI('', apiPaths.editLock, { data: { lessonID: lesson._id, userID: user && user._id, action: 'forceRelease' } });
    };

    const showRestoreDialog = () => updateLessonState({ showBackupDialog: true });

    const restoreBackups = (backups) => {
        if (backups.length > 0) {
            backups.forEach(async backup => {
                const data = { data: { id: backup._id } };
                const response = await httpAPI(apiServer, apiPaths.getBackup, data);
                const { nodeStruct } = response.data.backup;
                // clear nodes of old object & figma IDs & allocate a new guid
                const nodesToSave = refreshNodesInNodeMap(nodeStruct.nodeMap, { root: nodeStruct.root, lessonUpdates: { status: 'Editing' }, lessonTitlePrefix: 'Restored ' });
                await updateDBNodeMap([...nodesToSave]);
                if (nodeStruct.lessonSpec) {
                    await httpAPI('', `${apiPaths.postSpec}/${nodeStruct.lessonSpec.lessonID}`, { data: { specification: nodeStruct.lessonSpec.specification } });
                }
            });
        }
    };

    const pasteLesson = async () => {
        try {
            const response = await httpAPI(apiServer, apiPaths.getClipboard, { data: { userID: user._id } });
            const { nodeStruct } = response.data;
            if (nodeStruct.root && nodeStruct.nodeMap[nodeStruct.root].nodeClass === 'lesson') {
                const nodesToSave = refreshNodesInNodeMap(nodeStruct.nodeMap, { root: nodeStruct.root, lessonTitlePrefix: 'Pasted ' });
                updateDBNodeMap([...nodesToSave]);
            } else {
                setShowAlert(true, 'Copy/paste clipboard does not containt a lesson, not pasted');
            }
        } catch (e) {
            setShowAlert(true, 'Copy/paste clipboard does not containt a lesson, not pasted');
        }
    };

    const importLesson = () => {
        const inputForm = document.getElementById('file-dialog-form-id');
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'text/json');
        inputForm.appendChild(input);
        const loadImport = async () => {
            try {
                const json = await input.files[0].text();
                inputForm.removeChild(input);
                const nodeStruct = JSON.parse(json);
                if (nodeStruct.root && nodeStruct.nodeMap[nodeStruct.root].nodeClass === 'lesson') {
                    const nodesToSave = refreshNodesInNodeMap(nodeStruct.nodeMap, { root: nodeStruct.root, lessonTitlePrefix: 'Uploaded ' });
                    await updateDBNodeMap([...nodesToSave], null, { alreadyFlat: true, applyFixups: true, nodeMap: nodeStruct.nodeMap });
                    if (nodeStruct.lessonSpec) {
                        await httpAPI('', `${apiPaths.postSpec}/${nodeStruct.lessonSpec.lessonID}`, { data: { specification: nodeStruct.lessonSpec.specification } });
                    }
                } else {
                    setShowAlert(true, 'Uploaded file does not contain a lesson, not imported');
                }
            } catch (e) {
                // what to do with errors?
            }
        };
        input.addEventListener('input', loadImport);
        input.click();
    };

    const newLessonFromType = (type) => {
    };

    const newLessonFromTemplate = async (template) => {
        // load & clone template lesson
        const { lesson: newLessonObject, editLock } = await loadLessonObject(template._id);
        const lesson = newLessonObject.nodeMap[newLessonObject.lessonNode.nodeID];
        lesson.status = 'Editing'; lesson.title = `New ${lesson.title}`;
        const nodesToSave = refreshNodesInNodeMap(newLessonObject.nodeMap, { root: lesson.nodeID });
        updateDBNodeMap([...nodesToSave]);
    };

    const updateFigmaTemplateCopy = async () => {
        setShowAlert(true, 'Getting latest templates, please wait...', 1000, true);
        await getFigmaTemplates(true);
        setShowAlert(false);
    };

    const conformOneLesson = async (lessonObject, options) => {
        const { lesson, editLock } = await loadLessonObject(lessonObject._id);
        if (editLock) {
            setShowAlert(true, `Can't conform ${lesson.title} as it is currently being edited by ${editLock.editor.name}`);
        } else if (lesson) {
            if (!(options && options.noAlerts)) setShowAlert(true, 'Conforming, please wait...', 0, true);
            const rootNode = lesson.nodeMap[lesson.lessonNode.nodeID];
            wireNodeTree(rootNode, lesson.nodeMap);
            const nodesToSave = new Set();
            conformToFigmaLayout(rootNode, nodesToSave);
            if (nodesToSave.size > 0) {
                updateDBNodeMap([...nodesToSave], [], { action: 'conform', dontLogUpdate: true, ...(options || {}) });
            }
            if (!(options && options.noAlerts)) {
                setShowAlert(false);
                setShowAlert(true, `${lesson.title} template conforming complete.`);
            }
        }
    };

    const conformCallback = async () => {
        await getFigmaTemplates();
        const lessons = [...lessonState.allLessons];
        setShowAlert(true, 'Conforming all lessons, please wait...', 0, true);
        const conformNext = async () => {
            if (lessons.length > 0) {
                const [lessonObject] = lessons.splice(0, 1);
                console.log(`Conforming ${lessonObject.title}, ${lessons.length} to go...`);
                await conformOneLesson(lessonObject, { noAlerts: true });
                setTimeout(conformNext, 1000);
            } else {
                setShowAlert(false);
                setShowAlert(true, 'All lessons conformed');
            }
        };
        conformNext();
    };

    const conformAllLessons = () => {
        updateLessonState({
            showConfirm: true,
            confirmMessage: 'Please confirm updating all lessons to latest Figma templates',
            confirmCallback: conformCallback,
        });
    };

    const backupAllLessons = () => {
        setShowAlert(true, 'Backing up all lessons, please wait...', 0, true);
        Promise.all(lessonState.allLessons.map(lesson => {
            loadAndBackupLesson(lesson, user);
        })).then(() => setShowAlert(true, 'All lessons backed up'));
    };

    const backup = async (lesson) => {
        await loadAndBackupLesson(lesson, user);
        setShowAlert(true, `Lesson ${lesson.title} backed up`);
    };

    const reset = async (lesson) => {
        await httpAPI('', `${apiPaths.resetProgress}/${lesson._id}`, { data: {} });
        loadLessonLists();
    };

    const resetAllForMe = async () => {
        httpAPI('', `${apiPaths.resetProgress}/*`, { data: {} })
            .then(() => loadLessonLists());
    };

    const conform = async (lesson) => {
        await getFigmaTemplates();
        conformOneLesson(lesson);
    };

    const copy = async (lesson) => {
        const { data = {} } = await httpAPI(apiServer, apiPaths.lesson, { data: { lessonID: lesson._id } });
        const { lesson: lessonObject } = data;
        if (lessonObject) {
            const nodeStruct = { root: lessonObject.lessonNode.nodeID, nodeMap: lessonObject.nodeMap, timestamp: Date.now() };
            const data = { data: { userID: user._id, nodeStruct } };
            httpAPI(apiServer, apiPaths.saveClipboard, data);
        }
    };

    const duplicate = async (lesson) => {
        const { lesson: newLessonObject } = await loadLessonObject(lesson._id);
        const newLesson = newLessonObject.nodeMap[newLessonObject.lessonNode.nodeID];
        newLesson.status = 'Editing'; newLesson.title = `${newLesson.title} copy`;
        const nodesToSave = refreshNodesInNodeMap(newLessonObject.nodeMap, { root: newLesson.nodeID });
        updateDBNodeMap([...nodesToSave]);
    };

    const replace = async (lesson) => {
        // replace same-titled, published lesson with this one, which has to be in final-review state
        const replacee = lessonState.allLessons.find(rl => !rl.replaced && rl.lessonNode.status === 'Published'
            && rl.lessonNode.number === lesson.lessonNode.number && rl.lessonNode.group === lesson.lessonNode.group && rl.title === lesson.title);
        if (replacee && !lesson.private && lesson.lessonNode.status === 'Final review') {
            replacee.replaced = true;
            changeLessonFlag(replacee, 'replaced', true);
            updateLessonNodeField(lesson, 'status', 'Published');
            lesson.lessonNode.publishDate = replacee.lessonNode.publishDate;
            lesson.lessonNode.replaces = replacee._id;
            // reassign any assigned pattern to this lesson
            if (replacee.lessonNode.patternID) {
                const { lesson: lessonObject } = await loadLessonObject(lesson._id);
                const data = { data: { lessonID: lessonObject._id, patternID: replacee.lessonNode.patternID } };
                await httpAPI(apiServer, apiPaths.setLesson, data);
                lesson.lessonNode.patternID = replacee.lessonNode.patternID;
            }
            updateDBNodeMap([lesson.lessonNode]);
            setShowAlert(true, `Existing lesson ${lesson.title} replaced with this one, now placed in Published state`);

        } else {
            setShowAlert(true, 'This lesson must be in Final review and not private, and there must be a published lesson with same title, group & number to replace');
        }
    };

    // the start section pages have been deprecated in the regular player flow since all the info on
    // these pages is now in the lesson overview page that students always see when entering a lesson
    // For now, all the pages in the first section in a lesson if it is of type 'Start' will be skipped automatically
    // when a lesson is played and marked as having been visited in the lesson activity trackers
    // However, they will be visible if the lesson editor is open, so lesson editors can update the content

    const play = async (lesson) => {
        globalContext.update({ showEditor: false, showExitConfirmDialog: false });
        const myLessonToPlay = lessonState.myLessonMap[lesson._id] || lesson;
        const { lesson: lessonToPlay } = await loadLessonObject(lesson._id);
        const state = myLessonToPlay.lessonTracker ? myLessonToPlay.lessonTracker.state : 'start';
        const action = stateMap[state].action;
        await postLessonEvent(lessonToPlay, { action, context: { lesson: lessonToPlay.lessonNode.nodeID } });
        // skip deprecated start session pages
        if (lessonToPlay.lessonNode.group !== 'Starter' && (action === 'start' || action === 'review')) {
            // emulate playing of the hidden pages
            const sections = lessonToPlay.lessonNode.children.map(c => lessonToPlay.nodeMap[c]);
            let section = sections[0];
            if (section.type === 'Start') {
                let toSection = section;
                let pages = section.children.map(c => lessonToPlay.nodeMap[c]);
                if (!pages.find(p => p.componentName === 'LessonGoalWithImage')) {
                    let toPage;
                    for await (const page of pages) {
                        const stepID = page.children[0];
                        if (page.index < pages.length - 1) {
                            toPage = lessonToPlay.nodeMap[pages[page.index + 1].nodeID];
                        } else {
                            toSection = sections[1];
                            toPage = lessonToPlay.nodeMap[toSection.children[0]];
                        }
                        const toStepID = toPage.children[0];
                        await postLessonEvent(lessonToPlay, {
                            action: 'next',
                            context: { step: stepID, page: page.nodeID, section: section.nodeID },
                            details: {
                                to: { step: toStepID, page: toPage.nodeID, section: toSection.nodeID }
                            }
                        });
                    }
                    // construct next page URL & head there
                    const link = `${lessonsPaths.player}/${lessonToPlay._id}/${toPage.nodeID}`;
                    window.location.href = link;
                    return;
                }
            }
        }
        window.location.href = lessonLink(lessonToPlay);  // make visible to any embedding app
    };

    const download = async (lesson) => {
        await loadAndExportLesson(lesson, user);
        // setShowAlert(true, `Lesson ${lesson.title} downloaded`);
    };

    const downloadAllLessons = async () => {
        await httpAPI('', apiPaths.exportAll, { data: { } });
        setShowAlert(true, 'Lessons downloaded');
    };

    // assign lesson in lessonState.lessonToAssign to given pattern
    const assignToGrammarPattern = async (pattern) => {
        const { lesson } = await loadLessonObject(lessonState.lessonToAssign._id);
        const data = { data: { lessonID: lesson._id, patternID: pattern.id } };
        await httpAPI(apiServer, apiPaths.setLesson, data);
        lesson.lessonNode.patternID = pattern.id;
        updateDBNodeMap([lesson.lessonNode]);
    };

    const showAssignPatternDialog = (lesson) => updateLessonState({ showAssignPatternDialog: true, lessonToAssign: lesson });

    // checks for lesson-tree consistency
    const repairLessonTree = async (lesson) => {
        const { lesson: lessonObject, editLock } = await loadLessonObject(lesson._id);
        if (editLock) {
            setShowAlert(true, `Can't check ${lesson.title}, currently being edited by ${editLock.editor.name}`);
        } else if (lessonObject) {
            setShowAlert(true, 'Checking lesson(s), please wait...', 0, true);
            const rootNode = lessonObject.nodeMap[lessonObject.lessonNode.nodeID];
            wireNodeTree(rootNode, lessonObject.nodeMap);
            const [fixes, changedNodes, deletedNodes] = checkAndRepairLessonTree(lesson);
            if (changedNodes.length > 0 || deletedNodes.length > 0) {
                // apply fixes
                setShowAlert(false);
                setShowAlert(true, `${fixes.join(', ')} - saving fixes...`);
            } else
                setShowAlert(true, 'No issues found');
        }
    };

    const changeLessonFlag = (lesson, flag, value) => {
        const data = { data: { lessonID: lesson._id, flag, value } };
        return httpAPI(apiServer, apiPaths.updateLessonFlag, data);
    };

    const assignEditor = (lesson, editor, user) => {
        const assignee = editor || { _id: '', name: '', role: '' };
        assignee.assignedBy = { _id: user._id, name: user.name, role: user.role };
        assignee.date = Date.now();
        lesson.editor.assignedTo = assignee;
        const data = { data: { lessonID: lesson._id, assignee } };
        httpAPI(apiServer, apiPaths.assignEditor, data);
        if (!lesson.lessonNode.status.match(/Coming soon/i)) {
            lesson.private = true;
            changeLessonFlag(lesson, 'private', true);
        }
    };

    const globalSearchAndReplace = () => {

    };
    const showGlobalSRDialog = () => updateLessonState({ showGlobalSRDialog: true });

    // deprecated
    const changeLessonSet = (lesson, set) => {
        const data = { data: { lessonID: lesson._id, set } };
        httpAPI(apiServer, apiPaths.updateLessonSet, data);
    };

    const newLesssonsSubMenus = () => [
        // { label: 'Built-in lessons', submenu: [{ label: '(Not available here yet)', handler: () => {} }] },
        // { label: 'Built-in lessons', submenu: lessonTypes.filter(t => t.type !== 'Additional pages')
        //     .map(t => ({ label: t.type, handler: () => newLessonFromType(lessonTypeMap[t.type]) })) },
        { label: 'Template lessons', submenu: lessonState.allLessons.filter(lesson => lesson.lessonNode.status === 'Template' && !lesson.title.match(/page/i))
            .map(lesson => ({ label: lesson.title, handler: () => newLessonFromTemplate(lesson) })) },
    ];

    const adminMenu = () => [
        { label: 'Add new lesson', disabled: !lessonCreator(), submenu: newLesssonsSubMenus() },
        { divider: true },
        { label: 'Paste lesson', handler: pasteLesson },
        { label: 'Upload lesson', handler: importLesson },
        { label: 'Global search and replace', disabled: !adminUser(), handler: showGlobalSRDialog },
        { divider: true },
        { label: 'Backup all lessons', handler: backupAllLessons },
        { label: 'Restore selected backups...', handler: showRestoreDialog },
        // { label: 'Download all lessons', handler: downloadAllLessons },  todo: implement this, download as a zip
        { divider: true },
        { label: 'Reset progress on all my lessons', handler: resetAllForMe },
        { divider: true },
        { label: 'Get latest Figma templates', disabled: !adminUser(), handler: updateFigmaTemplateCopy },
        { label: 'Conform all lessons to Figma templates', disabled: !adminUser(), handler: conformAllLessons },
    ];

    const adminListContextMenuIems = () => [
        { label: 'Play lesson', handler: play },
        { label: 'Edit lesson', handler: editLesson },
        { divider: true },
        { label: 'Backup lesson', handler: backup },
        { label: 'Download lesson', handler: download },
        { divider: true },
        { label: 'Reset lesson progress for this user', handler: reset },
        { divider: true },
        { label: 'Copy lesson', handler: copy },
        { label: 'Duplicate lesson', handler: duplicate },
        { label: 'Replace published lesson', disabled: !adminUser(), handler: replace },
        { divider: true },
        { label: 'Edit JIRA ticket', handler: lesson => updateLessonState({ editJIRA: lesson.lessonNode.nodeID }) },
        { label: 'Assign lesson to Grammar Pattern...', disabled: !adminUser(), handler: showAssignPatternDialog },
        { label: 'Conform lesson to Figma layout', disabled: !adminUser(), handler: conform },
        // { label: 'Check & repair lesson', handler: repairLessonTree },  todo:  need to add db update!  fix & re-enable this!
        { divider: true },
        { label: 'Copy lesson title to clipboard', handler: lesson => navigator.clipboard.writeText(lesson.title) },
        { label: 'Copy lesson ID to clipboard', handler: lesson => navigator.clipboard.writeText(`ObjectId("${lesson._id.toString()}")`) },
        { label: 'Copy lesson URL to clipboard', handler: lesson => navigator.clipboard.writeText(`${window.location.origin}${lessonsPaths.player}/${lesson._id}`) },
    ];

    // LessonsContext value
    return {
        ...lessonState,
        update: updateLessonState,
        initialize,
        loadLessonLists,
        editLesson,
        buildLessonLists,
        updateLessonNodeField,
        guestMode, adminUser, lessonEditor,
        showRestoreDialog,
        setShowAlert,
        setShowConfirm: (state) => updateLessonState({ showConfirm: state }),
        adminMenu,
        adminListContextMenuIems,
        assignToGrammarPattern,
        restoreBackups,
        queueNodeMapUpdate,
        updateDBNodeMap,
        changeLessonFlag,
        changeLessonSet, // deprecated, now various flag fields
        assignEditor,
        backup,
        reset,
        conform,
        play,
        download,
        releaseEditLock,
    };
}

export default LessonsContext;




